import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CalendarToday, CodeRounded, Facebook, Instagram, Refresh, WhatsApp } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  summary: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fafafa",
  },
  channelCard: {
    height: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
  },
  channelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  channelIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flex: "none",
  },
  mono: { fontFamily: "monospace" },
}));

function _has(obj, key) {
  return obj && Object.prototype.hasOwnProperty.call(obj, key);
}

const ACTIVE_CONNECTION_STATUSES = new Set([
  "active",
  "connected",
  "pairing",
  "opening",
  "timeout",
  "online",
  "ok",
]);

function isConnectionActive(connection) {
  if (!connection) return false;

  const status = String(connection?.status || connection?.config?.status || "")
    .trim()
    .toLowerCase();

  if (connection?.isActive === true) return true;
  if (ACTIVE_CONNECTION_STATUSES.has(status)) return true;

  // Backward-compatible fallback for older rows where state flags were not normalized.
  if (connection?.channel === "whatsapp") {
    return Boolean(
      connection?.phoneNumberId ||
      connection?.config?.phoneNumberId ||
      connection?.config?.token ||
      connection?.externalAccountId
    );
  }

  if (connection?.channel === "facebook") {
    return Boolean(connection?.pageId || connection?.externalAccountId);
  }

  if (connection?.channel === "instagram") {
    return Boolean(connection?.instagramAccountId || connection?.externalAccountId);
  }

  return false;
}

export default function ConfigApis() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [loading, setLoading] = useState(false);
  const [planConfig, setPlanConfig] = useState(null);
  const [connections, setConnections] = useState([]);

  const entitlements = useMemo(() => {
    return planConfig?.entitlements || planConfig?.plan?.entitlements || {};
  }, [planConfig]);

  const allowWhatsapp = useMemo(() => {
    if (_has(entitlements, "whatsapp")) return !!entitlements.whatsapp;
    if (typeof planConfig?.useWhatsapp === "boolean") return planConfig.useWhatsapp;
    return true;
  }, [entitlements, planConfig]);

  const allowFacebook = useMemo(() => {
    if (_has(entitlements, "facebook")) return !!entitlements.facebook;
    if (typeof planConfig?.useFacebook === "boolean") return planConfig.useFacebook;
    return true;
  }, [entitlements, planConfig]);

  const allowInstagram = useMemo(() => {
    if (_has(entitlements, "instagram")) return !!entitlements.instagram;
    if (typeof planConfig?.useInstagram === "boolean") return planConfig.useInstagram;
    return true;
  }, [entitlements, planConfig]);

  const planConnectionsLimit = useMemo(() => {
    const v = planConfig?.connections ?? planConfig?.plan?.connections;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [planConfig]);

  const counts = useMemo(() => {
    const active = (connections || []).filter(isConnectionActive);
    const whatsapp = active.filter((c) => c?.channel === "whatsapp").length;
    const facebook = active.filter((c) => c?.channel === "facebook").length;
    const instagram = active.filter((c) => c?.channel === "instagram").length;
    return {
      whatsapp,
      facebook,
      instagram,
      total: whatsapp + facebook + instagram,
          whatsappPhone: active.filter((c) => c?.channel === "whatsapp").map((c) => c?.phoneNumber || c?.phone_number || "").filter(Boolean).join(", "),
    };
  }, [connections]);

  const load = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const [plan, conns] = await Promise.all([
        getPlanCompany(undefined, user.companyId),
        api.get("/channel-connections", {
          params: { channels: JSON.stringify(["whatsapp", "facebook", "instagram"]) },
        }),
      ]);

      setPlanConfig(plan || null);
      setConnections(Array.isArray(conns?.data) ? conns.data : []);
    } catch (err) {
      toastError(err);
      setConnections([]);
      setPlanConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ChannelCard = ({ title, subtitle, icon, iconBg, count, onConfigure, disabled }) => (
    <Card className={classes.channelCard} elevation={0}>
      <CardContent>
        <div className={classes.channelHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className={classes.channelIcon} style={{ background: iconBg }}>
              {icon}
            </div>
            <div>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            </div>
          </div>
        </div>

        <Typography variant="body2" color="textSecondary">
          {i18n.t("configApis.channelCard.activeConnections")}{" "}
          <span className={classes.mono}>{String(count ?? 0)}</span>
        </Typography>
        {planConnectionsLimit !== null && (
          <Typography variant="body2" color="textSecondary">
            {i18n.t("configApis.summary.planLimit")}{" "}
            <span className={classes.mono}>{String(planConnectionsLimit)}</span>
          </Typography>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button variant="contained" color="primary" onClick={onConfigure}>
            {i18n.t("configApis.channelCard.configure")}
          </Button>
          <Button variant="outlined" onClick={() => history.push("/subscription")} disabled={disabled}>
            {i18n.t("configApis.channelCard.upgradePlan")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const limitReached = planConnectionsLimit !== null ? counts.total >= planConnectionsLimit : false;

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("configApis.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={load}
            disabled={loading}
          >
            {i18n.t("configApis.actions.refresh")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.summary}>
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("configApis.summary.title")}
          </Typography>
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("configApis.summary.activeConnections")}{" "}
                <span className={classes.mono}>
                  {String(counts.total)}
                  {planConnectionsLimit !== null ? ` / ${String(planConnectionsLimit)}` : ""}
                </span>
              </Typography>
              {planConnectionsLimit !== null && limitReached && (
                <Typography variant="body2" style={{ marginTop: 6, color: "#b45309" }}>
                  {i18n.t("configApis.summary.limitReached")}
                </Typography>
              )}
            </>
          )}
        </div>

        <Grid container spacing={2}>
          {allowWhatsapp && (
            <Grid item xs={12} md={4}>
              <ChannelCard
                title={i18n.t("configApis.channels.whatsappTitle")}
                subtitle={`${i18n.t("configApis.subtitles.whatsapp")}${counts.whatsappPhone ? " | Tel: " + counts.whatsappPhone : ""}`}
                icon={<WhatsApp />}
                iconBg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                count={counts.whatsapp}
                onConfigure={() => history.push("/config-apis/whatsapp")}
                disabled={false}
              />
            </Grid>
          )}

          {allowFacebook && (
            <Grid item xs={12} md={4}>
              <ChannelCard
                title={i18n.t("configApis.channels.facebookTitle")}
                subtitle={i18n.t("configApis.subtitles.facebook")}
                icon={<Facebook />}
                iconBg="linear-gradient(135deg, #1877f2 0%, #0d5dbf 100%)"
                count={counts.facebook}
                onConfigure={() => history.push("/config-apis/facebook")}
                disabled={false}
              />
            </Grid>
          )}

          {allowInstagram && (
            <Grid item xs={12} md={4}>
              <ChannelCard
                title={i18n.t("configApis.channels.instagramTitle")}
                subtitle={i18n.t("configApis.subtitles.instagram")}
                icon={<Instagram />}
                iconBg="linear-gradient(135deg, #e1306c 0%, #c13584 100%)"
                count={counts.instagram}
                onConfigure={() => history.push("/config-apis/instagram")}
                disabled={false}
              />
            </Grid>
          )}

          {/* Google Calendar */}
          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.channelCard} elevation={0}>
              <CardContent>
                <div className={classes.channelHeader}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className={classes.channelIcon} style={{ background: "linear-gradient(135deg, #4285F4 0%, #34A853 100%)" }}>
                      <CalendarToday />
                    </div>
                    <div>
                      <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Google Calendar
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Agendar citas desde WhatsApp
                      </Typography>
                    </div>
                  </div>
                </div>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, marginBottom: 12 }}>
                  Conecta tu Google Calendar para que tus clientes agenden citas directamente desde WhatsApp.
                </Typography>
                <Button
                  variant="contained"
                  style={{ borderRadius: 8, textTransform: "none", background: "#4285F4", color: "#fff" }}
                  onClick={async () => {
                    try {
                      const { data } = await api.get("/integrations/google-calendar/auth-url");
                      if (data.authUrl) window.location.href = data.authUrl;
                    } catch (err) {
                      toastError(err);
                    }
                  }}
                >
                  Conectar Google Calendar
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {!allowWhatsapp && !allowFacebook && !allowInstagram && (
            <Grid item xs={12}>
              <Card className={classes.channelCard} elevation={0}>
                <CardContent>
                  <div className={classes.channelHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className={classes.channelIcon} style={{ background: "#111827" }}>
                        <CodeRounded />
                      </div>
                      <div>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                          {i18n.t("configApis.empty.title")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {i18n.t("configApis.empty.subtitle")}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <Button variant="contained" color="primary" onClick={() => history.push("/subscription")}>
                    {i18n.t("configApis.empty.button")}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>
    </MainContainer>
  );
}
